import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConsentPicker } from "@/components/ConsentPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSignature, Upload, Trash2, MousePointerClick, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const TemplateEditor = ({ tpl, keys, onClose, onSaved }) => {
  const [page, setPage] = useState(1);
  const [fields, setFields] = useState(tpl.fields || []);
  const [key, setKey] = useState("nome_completo");
  const [size, setSize] = useState(9);
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const imgRef = useRef(null);
  const pg = tpl.pages[page - 1] || { w: 595, h: 842 };
  const label = (k) => keys.find((x) => x.key === k)?.label || k;

  useEffect(() => {
    let url;
    api.get(`/doc-templates/${tpl.id}/page/${page}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setImg(url); });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [tpl.id, page]);

  const add = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * pg.w;
    const y = ((e.clientY - r.top) / r.height) * pg.h;
    if (key === "testo" && !text.trim()) { toast.error("Scrivi il testo fisso da inserire"); return; }
    setFields([...fields, { id: `n${Date.now()}`, page, x: Math.round(x), y: Math.round(y), key, size: Number(size) || 9, text: key === "testo" ? text : null }]);
  };
  const save = async () => {
    try { await api.put(`/doc-templates/${tpl.id}/fields`, { fields }); toast.success("Posizioni salvate"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const here = fields.filter((f) => f.page === page);
  const isSig = (k) => k.startsWith("firma_");
  const [sel, setSel] = useState(null);
  const moveBy = (id, dx, dy) => setFields((fs) => fs.map((x) => (x.id === id ? { ...x, x: Math.round(x.x + dx), y: Math.round(x.y + dy) } : x)));
  const startDrag = (e, f) => {
    e.preventDefault(); e.stopPropagation(); setSel(f.id); document.activeElement?.blur();
    const r = imgRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ox = f.x, oy = f.y;
    const mv = (ev) => setFields((fs) => fs.map((x) => (x.id === f.id ? { ...x, x: Math.round(ox + ((ev.clientX - sx) / r.width) * pg.w), y: Math.round(oy + ((ev.clientY - sy) / r.height) * pg.h) } : x)));
    const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
  };
  useEffect(() => {
    if (!sel) return undefined;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName) || document.activeElement?.getAttribute("role") === "combobox") return;
      const st = e.shiftKey ? 5 : 1;
      const d = { ArrowLeft: [-st, 0], ArrowRight: [st, 0], ArrowUp: [0, -st], ArrowDown: [0, st] }[e.key];
      if (d) { e.preventDefault(); moveBy(sel, ...d); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel]);
  const ring = (f) => (sel === f.id ? " ring-2 ring-amber-400 z-10" : "");
  const pickKey = (k) => { setKey(k); if (isSig(k) && Number(size) < 20) setSize(35); if (!isSig(k) && Number(size) >= 20) setSize(9); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto" data-testid="tpl-editor">
        <DialogHeader><DialogTitle>Posiziona i campi · {tpl.name}</DialogTitle></DialogHeader>
        <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tpl-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <span>Pagina {page} di {tpl.pages.length}</span>
              <Button size="sm" variant="outline" disabled={page >= tpl.pages.length} onClick={() => setPage(page + 1)} data-testid="tpl-next-page"><ChevronRight className="h-4 w-4" /></Button>
              <span className="text-xs text-muted-foreground ml-2 flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5" />Clicca sul punto dove inizia il testo (sopra la riga) · trascina un campo per spostarlo, o selezionalo e usa le frecce</span>
            </div>
            <div className="relative border border-border inline-block w-full" data-testid="tpl-page">
              {img && <img ref={imgRef} src={img} alt="pagina" className="w-full cursor-crosshair select-none" onClick={add} draggable={false} />}
              {here.map((f) => isSig(f.key) ? (
                <span key={f.id} onPointerDown={(e) => startDrag(e, f)} data-testid={`tpl-field-${f.id}`} className={`absolute flex items-end rounded border-2 border-dashed border-emerald-500 bg-emerald-500/10 p-0.5 text-[10px] text-emerald-700 cursor-move touch-none select-none${ring(f)}`}
                  style={{ left: `${(f.x / pg.w) * 100}%`, top: `${((f.y - f.size) / pg.h) * 100}%`, width: `${((f.size * 3) / pg.w) * 100}%`, height: `${(f.size / pg.h) * 100}%` }}>{label(f.key)}</span>
              ) : (
                <span key={f.id} onPointerDown={(e) => startDrag(e, f)} data-testid={`tpl-field-${f.id}`} className={`absolute -translate-y-full whitespace-nowrap rounded bg-sky-500/85 px-1 text-[10px] text-white cursor-move touch-none select-none${ring(f)}`}
                  style={{ left: `${(f.x / pg.w) * 100}%`, top: `${(f.y / pg.h) * 100}%` }}>{f.key === "testo" ? `"${f.text}"` : label(f.key)}</span>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5"><span className="text-sm font-medium">Dato da inserire</span>
              <Select value={key} onValueChange={pickKey}>
                <SelectTrigger data-testid="tpl-key-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">{keys.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {key === "testo" && <Input placeholder="Testo fisso" value={text} onChange={(e) => setText(e.target.value)} data-testid="tpl-fixed-text" />}
            {isSig(key) && <p className="text-xs text-muted-foreground">Clicca dove deve iniziare la riga della firma (angolo in basso a sinistra del riquadro).</p>}
            <div className="flex items-center gap-2 text-sm">{isSig(key) ? "Altezza firma" : "Dimensione"} <Input type="number" className="w-20" value={size} onChange={(e) => setSize(e.target.value)} data-testid="tpl-font-size" /></div>
            <div className="rounded-md border border-border divide-y divide-border max-h-[50vh] overflow-y-auto text-sm" data-testid="tpl-field-list">
              {!here.length && <p className="p-3 text-xs text-muted-foreground">Nessun campo in questa pagina</p>}
              {here.map((f) => (
                <div key={f.id} onClick={() => { setSel(f.id); document.activeElement?.blur(); }} className={`flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 ${sel === f.id ? "bg-amber-50 dark:bg-amber-500/10" : ""}`} data-testid={`tpl-field-row-${f.id}`}>
                  <span className="truncate">{f.key === "testo" ? `"${f.text}"` : label(f.key)}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setFields(fields.filter((x) => x.id !== f.id))} data-testid={`tpl-del-field-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={save} data-testid="tpl-save-fields">Salva posizioni ({fields.length})</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DocTemplatesCard = () => {
  const [list, setList] = useState([]);
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const replRef = useRef(null);
  const [replId, setReplId] = useState(null);
  const load = () => api.get("/doc-templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/doc-templates/keys").then((r) => setKeys(r.data)); }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("name", name.trim() || file.name.replace(/\.pdf$/i, ""));
    try { await api.post("/doc-templates", fd); toast.success("Modello caricato: ora posiziona i campi"); setName(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento"); }
  };
  const replace = async (file) => {
    if (!file || !replId) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.put(`/doc-templates/${replId}/file`, fd); toast.success("Modello aggiornato: controlla che i campi siano ancora al posto giusto"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Eliminare il modello "${t.name}"?`)) return;
    await api.delete(`/doc-templates/${t.id}`); load();
  };

  return (
    <Card className="p-5 space-y-4" data-testid="doc-templates-card">
      <div>
        <h2 className="font-heading font-semibold flex items-center gap-2"><FileSignature className="h-4 w-4 text-primary" /> Modelli documenti da compilare</h2>
        <p className="text-xs text-muted-foreground">Carica i PDF (privacy, antiriciclaggio…), posiziona i campi una volta e scaricali compilati da ogni pratica.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input className="w-64" placeholder="Nome modello (es. Privacy Lite)" value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name-input" />
        <Button onClick={() => fileRef.current?.click()} data-testid="tpl-upload-btn"><Upload className="h-4 w-4 mr-2" />Carica PDF</Button>
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="tpl-file-input" />
        <input ref={replRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => { replace(e.target.files[0]); e.target.value = ""; }} />
      </div>
      <div className="divide-y divide-border rounded-md border border-border">
        {!list.length && <p className="p-4 text-sm text-muted-foreground">Nessun modello caricato</p>}
        {list.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`tpl-row-${t.id}`}>
            <div><div className="font-medium text-sm">{t.name}</div><div className="text-xs text-muted-foreground">{t.pages.length} pagine · {t.fields.length} campi posizionati</div></div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`tpl-edit-${t.id}`}><MousePointerClick className="h-4 w-4 mr-1.5" />Posiziona campi</Button>
              <Button size="sm" variant="outline" onClick={() => { setReplId(t.id); replRef.current?.click(); }} data-testid={`tpl-replace-${t.id}`}><RefreshCw className="h-4 w-4 mr-1.5" />Aggiorna PDF</Button>
              <Button size="icon" variant="ghost" onClick={() => del(t)} data-testid={`tpl-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      {editing && <TemplateEditor tpl={editing} keys={keys} onClose={() => setEditing(null)} onSaved={load} />}
    </Card>
  );
};

export const CompiledDocsButton = ({ practiceId }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [ask, setAsk] = useState(null);
  const [cons, setCons] = useState({});
  useEffect(() => { api.get("/doc-templates").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.length) return null;
  const dl = async (t, values) => {
    setBusy(t.id); setAsk(null);
    try {
      const r = await api.get(`/practices/${practiceId}/compile/${t.id}`, { responseType: "blob", params: values ? { consents: JSON.stringify(values) } : {} });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `${t.name}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Compilazione non riuscita"); } finally { setBusy(null); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="compiled-docs-card">
      <h2 className="font-heading font-semibold flex items-center gap-2"><FileSignature className="h-4 w-4 text-primary" /> Documenti da compilare</h2>
      <p className="text-xs text-muted-foreground">Compilati con i dati di pratica e anagrafica; i dati mancanti vengono letti dagli allegati (la prima volta può richiedere qualche secondo).</p>
      <div className="flex flex-wrap gap-2">
        {list.map((t) => (
          <Button key={t.id} variant="outline" size="sm" disabled={!!busy || !t.fields.length} onClick={() => (t.consents?.length ? (setCons({}), setAsk(t)) : dl(t))} data-testid={`compile-${t.id}`}
            title={t.fields.length ? "" : "Posiziona prima i campi del modello nella sezione Modelli documenti"}>
            {busy === t.id ? "Compilazione…" : t.fields.length ? `Scarica ${t.name} compilato` : `${t.name} · campi da posizionare`}
          </Button>
        ))}
      </div>
      {ask && (
        <Dialog open onOpenChange={(o) => !o && setAsk(null)}>
          <DialogContent className="max-w-md" data-testid="compile-consents-dialog">
            <DialogHeader><DialogTitle>Consensi · {ask.name}</DialogTitle></DialogHeader>
            <ConsentPicker consents={ask.consents} value={cons} onChange={setCons} testid="compile-consents" />
            <DialogFooter><Button onClick={() => dl(ask, cons)} data-testid="compile-consents-download">Scarica compilato</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
